import { getDayjsLocaleByLanguage } from '@app/i18n/helpers';
import dayjs from '@app/utils/dayjs-setup';

/**
 * Date and time formatting for the card, in one place because four panels and three
 * drawers show the same instants and must show them the same way.
 *
 * Everything takes the language and, where it matters, an injectable "now" — a test
 * cannot rely on the machine's clock to decide what "today" means, and neither
 * should the code.
 */

/**
 * `Σήμερα`/`Αύριο`/`Χθες` when it applies, else the weekday, the date AND the year.
 *
 * The year is not optional here. The card reaches 120 days back and the office keeps
 * clients for years, so one list can hold rows from two of them — a bare "Wed 30/09"
 * beside "Tue 12/05" says nothing about which is older. The relative words keep their
 * place because today cannot be in the wrong year.
 */
export const formatRelativeDay = (
  instant: string,
  language: string,
  labels: { today: string; tomorrow: string; yesterday: string },
  now: Date = new Date()
): string => {
  const locale = getDayjsLocaleByLanguage(language);
  const moment = dayjs(instant).locale(locale);
  const today = dayjs(now).locale(locale);

  if (moment.isSame(today, 'day')) return labels.today;
  if (moment.isSame(today.add(1, 'day'), 'day')) return labels.tomorrow;
  if (moment.isSame(today.subtract(1, 'day'), 'day')) return labels.yesterday;

  return moment.format('ddd DD/MM/YYYY');
};

/** The relative day plus the time, which is how every visit is named on this page. */
export const formatVisitWhen = (
  instant: string,
  language: string,
  labels: { today: string; tomorrow: string; yesterday: string },
  now: Date = new Date()
): string =>
  `${formatRelativeDay(instant, language, labels, now)} ${dayjs(instant).locale(getDayjsLocaleByLanguage(language)).format('HH:mm')}`;

export const formatTime = (instant: string, language: string): string =>
  dayjs(instant).locale(getDayjsLocaleByLanguage(language)).format('HH:mm');

export const formatDate = (instant: string, language: string): string =>
  dayjs(instant).locale(getDayjsLocaleByLanguage(language)).format('DD/MM/YYYY');

/**
 * The short date, year included.
 *
 * It was day-and-month on the grounds that a month heading was always nearby, which
 * was not true of the places that ended up using it: "+76 since 30/06" under a vital
 * and the tick labels on its chart carry no heading at all.
 */
export const formatDayMonth = (instant: string, language: string): string =>
  dayjs(instant).locale(getDayjsLocaleByLanguage(language)).format('DD/MM/YYYY');

/**
 * A `YYYY-MM` timeline group as a month heading in the reader's language.
 *
 * Parsed as a local date rather than an instant: the key was built from local
 * months, and reading it back as UTC would title December's group "November" for
 * anyone east of Greenwich.
 */
export const formatMonthHeading = (month: string, language: string): string =>
  dayjs(`${month}-01`).locale(getDayjsLocaleByLanguage(language)).format('MMMM YYYY');

/** `+12` / `-3.5`, with a sign, for a change the reader is meant to compare against zero. */
export const formatSignedNumber = (value: number, language: string): string =>
  `${value > 0 ? '+' : ''}${new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(value)}`;

/**
 * The span the card covers, as `19/05 – 15/11/2026`.
 *
 * The year is stated once when both ends share it and on both ends when they do
 * not: a window that reads `01/12 – 01/03` leaves the reader to guess which way
 * round it runs.
 */
export const formatWindowRange = (from: string, to: string, language: string): string => {
  const locale = getDayjsLocaleByLanguage(language);
  const start = dayjs(from).locale(locale);
  const end = dayjs(to).locale(locale);
  const startFormat = start.year() === end.year() ? 'DD/MM' : 'DD/MM/YYYY';

  return `${start.format(startFormat)} – ${end.format('DD/MM/YYYY')}`;
};
